import React, { useMemo, useState, useEffect, useRef } from "react"
import { useViewport } from 'components/general/useViewPort'
import { ALL_URL, getDateTimeFormat } from 'helpers/utils'
import { useTable } from 'react-table'
import { Accordion } from 'react-bootstrap'
import { ColumnSession, ColumnTime, ColumnStatus, ColumnEvent, ColumnEventWithCompetitor, ColumnCompetitor, ColumnResult, ColumnLocation, ColumnOT } from "components/common/schedule/TableColumns"
import { useUrlFriendlyToParameters, useGetURL, useNavigateTo } from 'helpers/urlFriendly';
import { getLevelRSC, getEventRSC } from "helpers/rsc"
import Discipline from "components/presentation/Discipline"
import DateTime from "components/presentation/DateTime";


const getConfiguration = (breakPoint, params, getURL, isH2H) => {
  const isMobile = (breakPoint === "sm" || breakPoint === "xs")
  let configuration = {
    columns: [],
    sortBy: []
  };
  configuration.sortBy = [{ id: 'Session', desc: false }]
  configuration.columns = isMobile ? TableColumnsSM(params, getURL, isH2H) : TableColumns(params, getURL, isH2H)
  return configuration;
};

const SessionScheduleTable = ({ data, headerType, activeDiscipline, isH2H }) => {
  const { breakPoint } = useViewport();
  const accordElem = useRef(null);
  const day = data[0]?.startDate
  const [discipline, setDiscipline] = useState('')
  const [state, setState] = useState('')
  const params = useUrlFriendlyToParameters();
  const getURL = useGetURL()
  const config = useMemo(() => getConfiguration(breakPoint, params, getURL, isH2H), [breakPoint]);
  const sortedData = data.sort((a, b) => {
    const dateSort = a?.session?.startDate - b?.session?.startDate;
    const sessionSort = a?.session?.code?.localeCompare(b?.session?.code);
    const orderSort = a?.order - b?.order;
    const locationOrderSort = a?.location?.locationOrder - b?.location?.locationOrder;
    const startDate = new Date(a?.startDate) - new Date(b?.startDate);
    return sessionSort || dateSort || startDate || orderSort || locationOrderSort;
  });
  const [paramsUrl, setParamsUrl] = useState({});
  const [shouldNavigate, setShouldNavigate] = useState(false);
  useNavigateTo(paramsUrl, shouldNavigate, setShouldNavigate, 'results', state)
  //Prepare URL to navigate
  let paramsURL = { ...params }
  const showDiscipline = paramsURL["mirs_discipline"] === ALL_URL.ALL_DISC;

  useEffect(() => {
    if (data?.length > 0) {
      setDiscipline(data[0]?.discipline?.code);
    } else {
      setDiscipline('')
    }
  }, [data]);

  // to monitor activediscipline and scroll to it
  useEffect(() => {
    if (activeDiscipline?.current && discipline && (activeDiscipline.current === discipline)) {
      executeScroll();
    }
  }, [activeDiscipline, discipline]);

  const executeScroll = () =>
    accordElem?.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      // block: "end",
    });

  const handleClick = (props) => {
    const resultRSC = (props?.eventUnit?.resultRSC)
    const rsc = resultRSC || props?.eventUnit?.code;

    const disciplineCode = props?.discipline?.code;

    paramsURL["mirs_event"] = getEventRSC(rsc)
    paramsURL["mirs_rsc"] = rsc
    paramsURL['mirs_discipline'] = disciplineCode;

    //Add state is sports like CLB thar results are brackets. Send position inside of bracket
    //Only brackets with bracketPase FNL     
    const el = data.find(item => item.code === props?.eventUnit?.code)
    if (el) {
      setState({ state: { bracketPosition: el?.eventUnit?.phase?.order, bracketCode: 'FNL' } });
    }
    setParamsUrl(paramsURL);
    if (rsc.indexOf('TRNO') === -1 && rsc.indexOf('TMRY') === -1 && props?.status?.code !== "CANCELLED" && (props?.eventUnit?.type !== "NONE")) {
      setShouldNavigate(true);
    }
  };

  return (
    <Accordion className='schedule-container' defaultActiveKey="0" ref={accordElem}>
      <Accordion.Item eventKey="0">
        <Accordion.Header className='schedule-title' id={`table-${discipline}-${day}`}>
          {showDiscipline
            ? <Discipline code={discipline} className={'img-fluid page-header-disciplina float-start w-5'} />
            : <DateTime format={getDateTimeFormat('DayName-DayNum-MonthShort')} datetime={day} />
          }
        </Accordion.Header>
        <Accordion.Body className="p-0">
          <Table key={`table-session-date-${day}`} columns={config.columns} data={sortedData} rowClickHandler={handleClick} ariaLabelledby={`table-${discipline}-${day}`} />
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  )
}

function Table({ columns, data, rowClickHandler, ariaLabelledby }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data,
  })

  const isClickable = (props) => {
    return ((props?.code.indexOf('TRNO') === -1) && (props?.code.indexOf('TMRY') === -1) && (props?.status?.code !== "CANCELLED") && (props?.eventUnit?.type !== "NONE"));
  }

  var lastSession = ''
  return (
    <div className="shadow-scrollbox">
      <table {...getTableProps()} className="mirs-table table-schedule table " {...(ariaLabelledby && { 'aria-labelledby': ariaLabelledby })} >
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.filter(h => h.id !== 'Session').map((column, i) => (
                <th {...column.getHeaderProps()} className={column.headerClassName} key={`header-${i}`}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            prepareRow(row)
            const sessionCode = row.original?.session?.code
            const isGrouped = sessionCode !== lastSession
            if (isGrouped) {
              lastSession = sessionCode
            }
            return (
              <React.Fragment key={`rowgroup-${i}`}>
                {isGrouped &&
                  <tr>
                    <td colSpan={row.cells.length - 1} className="schedule-session">
                      {row.cells.find(c => c.column.id === 'Session').render('Cell')}
                    </td>
                  </tr>
                }
                <tr {...row.getRowProps()} onClick={() => rowClickHandler(row.original)} key={`row-${i}`} className={isClickable(row.original) ? 'clickable-row' : ''}>
                  {row.cells.filter(c => c.column.id !== 'Session').map(cell => {
                    return <td {...cell.getCellProps()} className={cell.column.className} key={`cell-${i}-${cell.column.id}`}>{cell.render('Cell')}</td>
                  })}
                </tr>
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default SessionScheduleTable;

function TableColumns(paramsURL, getURL, isH2H) {
  return (
    [
      ColumnSession({ disableSort: true }),
      ColumnTime({ disableSort: true }),
      ColumnEvent({ disableSort: true, paramsURL, getURL }),
      ColumnCompetitor({ paramsURL, isH2H }),
      ColumnResult({}),
      ColumnOT({}),
      ColumnStatus({}),
      ColumnLocation({ disableSort: true })
    ]
  )
}

function TableColumnsSM(paramsURL, getURL, isH2H) {
  return (
    [
      ColumnSession({ disableSort: true }),
      ColumnTime({ disableSort: true }),
      ColumnEventWithCompetitor({ disableSort: true, paramsURL, getURL, isH2H }),
      ColumnStatus({})
    ]
  )
}


